import React, { useEffect, useRef, useState } from 'react';
import { FiFileText, FiSend, FiX } from 'react-icons/fi';
import {
  listWhatsAppTemplates,
  sendWhatsAppTemplate,
  templateSendErrorText,
  type WhatsAppTemplateOption,
} from '../../services/WhatsAppTemplateSendService';
import { newMetaIdempotencyKey } from './metaText';
import './metaConversation.css';

interface Props {
  sectorId: number;
  contactId: number;
  /** Nome do cliente: preenche as variáveis automáticas (o campo continua editável). */
  contactName?: string | null;
  /** Depois de enviar (a mensagem chega à conversa pelo tempo real). */
  onSent?: () => void;
}

const fillBody = (t: WhatsAppTemplateOption, values: Record<string, string>) =>
  (t.body ?? '').replace(/\{\{(\d+)\}\}/g, (m, n: string) => (values[n]?.trim() ? values[n] : m));

/**
 * "Enviar modelo": fora das 24 h só vale um modelo aprovado pela Meta. Escolhe o modelo, preenche as variáveis (o nome do cliente já vem)
 * e envia com chave de idempotência (a mesma chave vale para a nova tentativa).
 */
export const WhatsAppTemplateSender: React.FC<Props> = ({ sectorId, contactId, contactName, onSent }) => {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState<WhatsAppTemplateOption[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string>('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const keyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open || templates !== null) return;
    let alive = true;
    listWhatsAppTemplates(sectorId)
      .then((items) => alive && setTemplates(items))
      .catch((e) => {
        if (!alive) return;
        setTemplates([]);
        setLoadError(templateSendErrorText(e));
      });
    return () => {
      alive = false;
    };
  }, [open, templates, sectorId]);

  const selected = templates?.find((t) => t.id === selectedId) ?? null;

  const choose = (id: string) => {
    setSelectedId(id);
    setError(null);
    keyRef.current = null;
    const t = templates?.find((x) => x.id === id);
    const next: Record<string, string> = {};
    for (const v of t?.variables ?? []) next[String(v.index)] = v.kind === 'lead' ? (contactName ?? '') : '';
    setValues(next);
  };

  const missing = selected ? selected.variables.filter((v) => !values[String(v.index)]?.trim()) : [];
  const canSend = !busy && !!selected && missing.length === 0;

  const send = async () => {
    if (!canSend || !selected) return;
    setBusy(true);
    setError(null);
    keyRef.current ??= newMetaIdempotencyKey();
    try {
      await sendWhatsAppTemplate({ sectorId, contactId, templateId: selected.id, values, idempotencyKey: keyRef.current });
      keyRef.current = null;
      setOpen(false);
      setSelectedId('');
      setValues({});
      onSent?.();
    } catch (e) {
      setError(templateSendErrorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" className="wa-template-open" onClick={() => setOpen(true)}>
        <FiFileText size={14} aria-hidden="true" /> Enviar modelo
      </button>
      {open && (
        <div className="wa-template-backdrop" role="dialog" aria-modal="true" aria-label="Enviar modelo">
          <div className="wa-template-modal">
            <div className="wa-template-head">
              <strong>Enviar modelo</strong>
              <button type="button" className="wa-template-close" aria-label="Fechar" onClick={() => setOpen(false)}>
                <FiX size={16} aria-hidden="true" />
              </button>
            </div>
            {templates === null && <p className="wa-template-hint">Carregando modelos…</p>}
            {loadError && <p className="wa-template-error" role="alert">{loadError}</p>}
            {templates !== null && templates.length === 0 && !loadError && (
              <p className="wa-template-hint">Nenhum modelo aprovado ainda. Crie um em Conexões › Modelos e espere a aprovação da Meta.</p>
            )}
            {templates !== null && templates.length > 0 && (
              <>
                <label className="wa-template-field">
                  Modelo aprovado
                  <select value={selectedId} onChange={(e) => choose(e.target.value)}>
                    <option value="">Escolha um modelo</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </label>
                {selected?.variables.map((v) => (
                  <label className="wa-template-field" key={v.index}>
                    {v.title}
                    <input
                      type="text"
                      value={values[String(v.index)] ?? ''}
                      placeholder={v.example}
                      onChange={(e) => setValues((prev) => ({ ...prev, [String(v.index)]: e.target.value }))}
                    />
                  </label>
                ))}
                {selected && (
                  <div className="wa-template-preview" aria-label="Prévia">
                    {selected.headerText && <div className="wa-template-preview-head">{selected.headerText}</div>}
                    <div>{fillBody(selected, values)}</div>
                    {selected.footer && <div className="wa-template-preview-foot">{selected.footer}</div>}
                  </div>
                )}
              </>
            )}
            {error && <p className="wa-template-error" role="alert">{error}</p>}
            <div className="wa-template-actions">
              <button type="button" className="meta-composer-send" disabled={!canSend} onClick={() => void send()}>
                <FiSend size={14} aria-hidden="true" /> {busy ? 'Enviando…' : 'Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
